import type { SweetSpotEvaluationResult } from '../sweetspot/types';

export type PreFracStageMode = 'engineering' | 'geometric' | 'custom';

export type StageClusterSettings = {
  clusterCount: number;
  clusterSpacing: number;
  clusterLength: number;
};

export type PreFracClusterDraft = {
  id: string;
  stageNumber: number;
  depth: number;
  top: number;
  bottom: number;
  score: number | null;
  source: 'sweet_spot_candidate' | 'geometric';
  reason: string;
};

export type ClusterSpacingWarning = {
  segmentIndex: number;
  requiredClusterCount: number;
  actualClusterCount: number;
  minClusterSpacing: number;
  segmentLength: number;
  warning: string;
};

export type PreFracStageDraft = {
  stageNumber: number;
  top: number;
  bottom: number;
  length: number;
  avgScore: number | null;
  reason: string;
  settings: StageClusterSettings;
  clusters: PreFracClusterDraft[];
  clusterWarning: ClusterSpacingWarning | null;
};

export type BuildPreFracStagesOptions = {
  evaluation?: SweetSpotEvaluationResult;
  intervalTop: number;
  intervalBottom: number;
  mode: PreFracStageMode;
  stageCount: number;
  targetStageLength: number;
  minStageLength?: number;
  maxStageLength?: number;
  formationBoundaries?: Array<{ depth: number; reason: string }>;
  geometricCondition?: 'stage_length' | 'stage_count' | 'cluster_layout';
  geometricClusterCount?: number;
  geometricClusterSpacing?: number;
  firstStage?: { enabled: boolean; top: number; bottom: number };
  customBoundaries: number[];
  defaultClusterSettings: StageClusterSettings;
  settingsByStage?: Record<number, StageClusterSettings | undefined>;
};

const round = (value: number, digits = 2) => Number(value.toFixed(digits));

function geometricIntervals(top: number, bottom: number, count: number) {
  const safeCount = Math.max(1, Math.round(count));
  const step = (bottom - top) / safeCount;
  return Array.from({ length: safeCount }, (_, index) => ({
    top: top + index * step,
    bottom: index === safeCount - 1 ? bottom : top + (index + 1) * step,
    reason: '几何分段：在当前压裂区域内均匀划分',
  }));
}

function scoreChangeAt(options:BuildPreFracStagesOptions,depth:number){
  const points=options.evaluation?.compositeSweetnessCurve.slice().sort((a,b)=>a.depth-b.depth)??[];
  if(points.length<2)return 0;
  const before=points.filter(point=>point.depth<=depth).slice(-3);
  const after=points.filter(point=>point.depth>=depth).slice(0,3);
  if(!before.length||!after.length)return 0;
  const mean=(rows:typeof points)=>rows.reduce((sum,row)=>sum+row.value,0)/rows.length;
  return Math.abs(mean(before)-mean(after));
}

/** Data-driven engineering partition: boundaries follow sweet-spot transitions,
 *  evaluation zones and interpreted formation interfaces; length is only a guardrail. */
function engineeringIntervals(options: BuildPreFracStagesOptions, top: number, bottom: number) {
  const minLength = Math.max(20, options.minStageLength ?? 50);
  const maxLength = Math.max(minLength, options.maxStageLength ?? 100);
  const targetLength = (minLength + maxLength) / 2;
  const curveDepths = options.evaluation?.compositeSweetnessCurve.map((point) => point.depth) ?? [];
  const recommended = options.evaluation?.recommendedStageBoundaries.flatMap((item) => [item.top, item.bottom]) ?? [];
  const heterogeneous = options.evaluation?.heterogeneityBreakpoints.map(item=>item.depth)??[];
  const formations = options.formationBoundaries?.map(item=>item.depth)??[];
  const intervals: Array<{top:number;bottom:number;reason:string}> = [];
  let cursor = top;
  while (bottom - cursor > maxLength) {
    const low = cursor + minLength;
    const high = Math.min(cursor + maxLength, bottom - minLength);
    const candidates = [...curveDepths, ...recommended, ...heterogeneous, ...formations]
      .filter((depth) => depth >= low && depth <= high)
      .filter((depth, index, rows) => index === rows.indexOf(depth));
    if (!candidates.length) candidates.push(Math.min(cursor + targetLength, high));
    const selected = candidates.reduce((best, depth) => {
      const change = scoreChangeAt(options, depth);
      const lengthPenalty = Math.abs(depth - cursor - targetLength) / targetLength * 0.08;
      const formationBonus = formations.some(item=>Math.abs(item-depth)<.5)?.18:0;
      const heterogeneousBonus = heterogeneous.some(item=>Math.abs(item-depth)<.5)?.14:0;
      const recommendedBonus = recommended.some(item=>Math.abs(item-depth)<.5)?.05:0;
      const score = change*1.8+formationBonus+heterogeneousBonus+recommendedBonus-lengthPenalty;
      return score > best.score ? {depth, score, change, formationBonus, heterogeneousBonus} : best;
    }, {depth:candidates[0],score:Number.NEGATIVE_INFINITY,change:0,formationBonus:0,heterogeneousBonus:0});
    const basis=selected.formationBonus?'地层解释界面':selected.heterogeneousBonus?'甜点分区突变':selected.change>=.04?'综合甜度明显变化':'长度约束内的最优品质边界';
    intervals.push({top:cursor,bottom:selected.depth,reason:`智能分段：段界依据${basis}确定，段长仅作 ${minLength}–${maxLength} m 安全约束`});
    const boundary=selected.depth;
    cursor = boundary;
  }
  if (bottom > cursor) {
    const remainder=bottom-cursor;
    if(remainder<minLength&&intervals.length){
      const previous=intervals[intervals.length-1];
      previous.bottom=bottom;
      previous.reason+='；末端短段已与相邻品质段合并';
    }else intervals.push({top:cursor,bottom,reason:'智能分段：末段按剩余甜点与地层区间归并'});
  }
  return intervals;
}

function resolveIntervals(options: BuildPreFracStagesOptions) {
  let { intervalTop: top, intervalBottom: bottom } = options;
  if (!(Number.isFinite(top) && Number.isFinite(bottom) && bottom > top)) return [];

  const fixedFirst = options.firstStage?.enabled
    && options.firstStage.top >= top
    && options.firstStage.bottom > options.firstStage.top
    && options.firstStage.bottom <= bottom
    ? {top:options.firstStage.top,bottom:options.firstStage.bottom,reason:'首段参数：采用单独设置的首段顶深与底深'}
    : null;
  if (fixedFirst) top = fixedFirst.bottom;

  if (options.mode === 'engineering' && options.evaluation) {
    const intervals = engineeringIntervals(options, top, bottom);
    return fixedFirst ? [fixedFirst, ...intervals] : intervals;
  }

  if (options.mode === 'custom') {
    const boundaries = [
      top,
      ...options.customBoundaries.filter((depth) => depth > top && depth < bottom),
      bottom,
    ].sort((a, b) => a - b);
    const unique = boundaries.filter((depth, index) => index === 0 || depth > boundaries[index - 1]);
    return unique.slice(0, -1).map((stageTop, index) => ({
      top: stageTop,
      bottom: unique[index + 1],
      reason: '自定义分段：采用用户设置的参考曲线边界',
    }));
  }

  const layoutLength = Math.max(20, (options.geometricClusterCount ?? 1) * (options.geometricClusterSpacing ?? 0));
  const requestedCount = options.geometricCondition === 'stage_count'
    ? options.stageCount
    : options.geometricCondition === 'cluster_layout'
      ? Math.ceil((bottom - top) / layoutLength)
      : Math.ceil((bottom - top) / Math.max(20, options.targetStageLength));
  const intervals = geometricIntervals(top, bottom, Math.max(1, requestedCount));
  return fixedFirst ? [fixedFirst, ...intervals] : intervals;
}

function averageScore(evaluation: SweetSpotEvaluationResult | undefined, top: number, bottom: number) {
  const values = evaluation?.compositeSweetnessCurve
    .filter((point) => point.depth >= top && point.depth <= bottom)
    .map((point) => point.value) ?? [];
  return values.length ? round(values.reduce((sum, value) => sum + value, 0) / values.length, 3) : null;
}

function nearestScore(evaluation: SweetSpotEvaluationResult | undefined, depth: number) {
  const curve = evaluation?.compositeSweetnessCurve;
  if (!curve?.length) return null;
  return curve.reduce((best, point) =>
    Math.abs(point.depth - depth) < Math.abs(best.depth - depth) ? point : best).value;
}

function buildClusters(
  stageNumber: number,
  top: number,
  bottom: number,
  settings: StageClusterSettings,
  evaluation?: SweetSpotEvaluationResult,
): { clusters: PreFracClusterDraft[]; warning: ClusterSpacingWarning | null } {
  const count = Math.max(1, Math.round(settings.clusterCount));
  const minSpacing = Math.max(0, settings.clusterSpacing);
  const segmentLength = Math.max(0, bottom - top);
  const candidates = (evaluation?.recommendedClusterCandidates ?? [])
    .filter((item) => item.depth >= top && item.depth <= bottom)
    .sort((a, b) => b.score - a.score);
  const selected: Array<{ depth: number; score: number | null; source: PreFracClusterDraft['source']; reason: string }> = [];
  const effectiveSpacing = Math.max(minSpacing, 1e-6);
  const normalizeDepth = (depth: number) => round(Math.min(bottom, Math.max(top, depth)));
  const canAdd = (depth: number) => {
    const normalizedDepth = normalizeDepth(depth);
    return selected.every(
      (item) => Math.abs(item.depth - normalizedDepth) >= effectiveSpacing,
    );
  };
  const addGeometric = (depth: number) => {
    const normalizedDepth = normalizeDepth(depth);
    if (selected.length >= count || !canAdd(normalizedDepth)) return;
    selected.push({
      depth: normalizedDepth,
      score: nearestScore(evaluation, normalizedDepth),
      source: 'geometric',
      reason: '候选甜点簇位不足，按满足最小簇间距的段内位置补充',
    });
  };

  candidates.forEach((candidate) => {
    const normalizedDepth = normalizeDepth(candidate.depth);
    if (selected.length >= count || !canAdd(normalizedDepth)) return;
    selected.push({
      depth: normalizedDepth,
      score: candidate.score,
      source: 'sweet_spot_candidate',
      reason: candidate.reason,
    });
  });

  const preferredGeometric = Array.from({ length: count }, (_, index) =>
    top + ((index + 1) * segmentLength) / (count + 1));
  preferredGeometric.forEach(addGeometric);

  const scanSteps = Math.max(100, count * 25);
  Array.from({ length: scanSteps + 1 }, (_, index) =>
    top + (index * segmentLength) / scanSteps).forEach(addGeometric);

  const fallbackSpacing = count <= 1 ? segmentLength : segmentLength / (count - 1);
  if (selected.length < count && fallbackSpacing + Number.EPSILON >= effectiveSpacing) {
    selected.splice(
      0,
      selected.length,
      ...Array.from({ length: count }, (_, index) => {
        const depth = normalizeDepth(count <= 1 ? (top + bottom) / 2 : top + index * fallbackSpacing);
        return {
          depth,
          score: nearestScore(evaluation, depth),
          source: 'geometric' as const,
          reason: '候选甜点簇位组合无法满足簇数，已按最小簇间距回退为均匀合法簇位',
        };
      }),
    );
  }

  const clusters = selected
    .sort((a, b) => a.depth - b.depth)
    .slice(0, count)
    .map((item, index) => {
      const halfLength = Math.max(0.1, settings.clusterLength) / 2;
      return {
        id: `stage-${stageNumber}-cluster-${index + 1}`,
        stageNumber,
        depth: round(item.depth),
        top: round(Math.max(top, item.depth - halfLength)),
        bottom: round(Math.min(bottom, item.depth + halfLength)),
        score: item.score == null ? null : round(item.score, 3),
        source: item.source,
        reason: item.reason,
      };
    });

  const warning = clusters.length < count ? {
    segmentIndex: stageNumber,
    requiredClusterCount: count,
    actualClusterCount: clusters.length,
    minClusterSpacing: round(minSpacing),
    segmentLength: round(segmentLength),
    warning: `第 ${stageNumber} 段当前段长不足，无法满足设定簇间距：要求 ${count} 簇，实际可布置 ${clusters.length} 簇，最小簇间距 ${round(minSpacing)} m。`,
  } : null;

  return { clusters, warning };
}

export function buildPreFracStages(options: BuildPreFracStagesOptions): PreFracStageDraft[] {
  return resolveIntervals(options).map((interval, index) => {
    const stageNumber = index + 1;
    const settings = options.settingsByStage?.[stageNumber] ?? options.defaultClusterSettings;
    const clusterResult = buildClusters(
      stageNumber, interval.top, interval.bottom, settings, options.evaluation,
    );
    return {
      stageNumber,
      top: round(interval.top),
      bottom: round(interval.bottom),
      length: round(interval.bottom - interval.top),
      avgScore: averageScore(options.evaluation, interval.top, interval.bottom),
      reason: interval.reason,
      settings,
      clusters: clusterResult.clusters,
      clusterWarning: clusterResult.warning,
    };
  });
}
